import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShoppingCart, Package, CreditCard, Eye, ShoppingBag, Plus, Minus, Save, Trash2 } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { showErrorToast } from '@/lib/toast-utils';
import { CartSelection } from './CartSelection';

interface CartFlyoutProps {
  children: React.ReactNode;
}

export function CartFlyout({ children }: CartFlyoutProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [cartName, setCartName] = useState('');
  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set());
  const [quantityInputs, setQuantityInputs] = useState<Record<string, string>>({});
  const [debounceTimers, setDebounceTimers] = useState<Record<string, ReturnType<typeof setTimeout>>>({});
  const { cart, totalItems, isLoading, savedCarts, createNewCart, updateCartName, updateCartItemQuantity, removeFromCart } = useCart();
  

  // Initialize and sync quantity inputs when cart changes
  useEffect(() => {
    if (cart?.items) {
      setQuantityInputs(prev => {
        const newQuantities: Record<string, string> = {};
        cart.items.forEach(item => {
          // Only update if we're not currently editing this item
          if (!debounceTimers[item.id]) {
            newQuantities[item.id] = item.quantity.toString();
          } else {
            // Keep the current input value if we're debouncing
            newQuantities[item.id] = prev[item.id] || item.quantity.toString();
          }
        });
        return newQuantities;
      });
    }
  }, [cart?.items]);

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const handleQuantityChange = async (itemId: string, newQuantity: number) => {
    setUpdatingItems(prev => new Set(prev).add(itemId));
    await updateCartItemQuantity(itemId, newQuantity);
    setUpdatingItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
  };

  const handleQuantityInputChange = (itemId: string, value: string) => {
    // Update the input display immediately
    setQuantityInputs(prev => ({ ...prev, [itemId]: value }));

    // Clear existing timer for this item
    if (debounceTimers[itemId]) {
      clearTimeout(debounceTimers[itemId]);
    }

    // Validate and set new timer
    const quantity = parseInt(value);
    if (!isNaN(quantity) && quantity > 0) {
      const timer = setTimeout(() => {
        handleQuantityChange(itemId, quantity);
      }, 750);
      
      setDebounceTimers(prev => ({ ...prev, [itemId]: timer }));
    }
  };

  const handleQuantityInputBlur = (itemId: string, value: string) => {
    // Clear debounce timer
    if (debounceTimers[itemId]) {
      clearTimeout(debounceTimers[itemId]);
    }

    const quantity = parseInt(value);
    if (!isNaN(quantity) && quantity > 0) {
      handleQuantityChange(itemId, quantity);
    } else {
      // Reset to current quantity if invalid
      const item = cart?.items.find(i => i.id === itemId);
      if (item) {
        setQuantityInputs(prev => ({ ...prev, [itemId]: item.quantity.toString() }));
      }
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    setUpdatingItems(prev => new Set(prev).add(itemId));
    const success = await removeFromCart(itemId);
    // Only remove from loading set if it failed (success means item is gone)
    if (!success) {
      setUpdatingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  const handleSaveCart = async () => {
    if (!cartName.trim()) {
      showErrorToast('Please enter a name for your cart.', 'Cart Name Required');
      return;
    }

    try {
      if (cart?.secretId) {
        // Update existing cart name
        const success = await updateCartName(cart.secretId, cartName.trim());
        if (success) {
          setShowSaveDialog(false);
          setCartName('');
        }
      } else {
        // Create new cart - this functionality may need to be extended
        await createNewCart();
        setShowSaveDialog(false);
        setCartName('');
      }
    } catch (error) {
      showErrorToast('Failed to save cart. Please try again.');
    }
  };

  const renderCartContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="text-center space-y-2">
            <Package className="h-8 w-8 text-muted-foreground mx-auto animate-pulse" />
            <p className="text-muted-foreground">Loading cart...</p>
          </div>
        </div>
      );
    }

    // Show cart selection if no active cart but there are saved carts
    if (!cart && savedCarts.length > 0) {
      return <CartSelection onClose={() => setIsOpen(false)} />;
    }

    // Empty cart state
    if (!cart || cart.items.filter(item => item.orderLineType === 'product').length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-8 space-y-4">
          <ShoppingBag className="h-12 w-12 text-muted-foreground" />
          <div className="text-center space-y-2">
            <h3 className="font-medium text-foreground">Your cart is empty</h3>
            <p className="text-sm text-muted-foreground">
              Add some items to get started
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full space-y-3">
        {/* Cart Items */}
        <ScrollArea className="flex-1 pr-2" type="always">
          <div className="space-y-2 pr-2">
            {cart.items.filter(item => item.orderLineType === 'product').map((item) => {
              // Find associated product discount for this item
              const productDiscount = cart.items.find(
                discount => discount.orderLineType === 'productDiscount' && 
                discount.productId === item.productId
              );
              
              return (
              <Card key={item.id} className={`border-border transition-opacity ${updatingItems.has(item.id) ? 'opacity-50' : 'opacity-100'}`}>
                <CardContent className="p-2">
                  <div className="flex items-start space-x-2">
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.productName}
                        className="w-10 h-10 object-cover rounded bg-muted flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-muted rounded flex items-center justify-center flex-shrink-0">
                        <Package className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-1 mb-0.5">
                        <h4 className="font-medium text-xs text-foreground truncate leading-tight">
                          {item.productName}
                        </h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 text-destructive hover:text-destructive-foreground hover:bg-destructive flex-shrink-0"
                          onClick={() => handleRemoveItem(item.id)}
                          disabled={updatingItems.has(item.id)}
                        >
                          <Trash2 className="h-2.5 w-2.5" />
                        </Button>
                      </div>
                      <p className="text-[10px] text-muted-foreground leading-tight">
                        {formatCurrency(item.unitPrice, cart.currencyCode)} each
                      </p>
                      {productDiscount && (
                        <p className="text-[9px] text-green-600 dark:text-green-400 leading-tight font-medium">
                          {productDiscount.productName}
                        </p>
                      )}
                      {/* Quantity and Price Row */}
                      <div className="flex items-center justify-between mt-1.5">
                        <div className="flex items-center border border-border rounded">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 hover:bg-muted"
                            onClick={() => {
                              const newQty = Math.max(1, item.quantity - 1);
                              setQuantityInputs(prev => ({ ...prev, [item.id]: newQty.toString() }));
                              handleQuantityChange(item.id, newQty);
                            }}
                            disabled={item.quantity <= 1 || updatingItems.has(item.id)}
                          >
                            <Minus className="h-2.5 w-2.5" />
                          </Button>
                          <Input
                            type="text"
                            value={quantityInputs[item.id] || item.quantity}
                            onChange={(e) => handleQuantityInputChange(item.id, e.target.value)}
                            onBlur={(e) => handleQuantityInputBlur(item.id, e.target.value)}
                            disabled={updatingItems.has(item.id)}
                            className="h-6 w-10 text-center border-0 focus-visible:ring-0 text-[10px] font-medium p-0"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 hover:bg-muted"
                            onClick={() => {
                              const newQty = item.quantity + 1;
                              setQuantityInputs(prev => ({ ...prev, [item.id]: newQty.toString() }));
                              handleQuantityChange(item.id, newQty);
                            }}
                            disabled={updatingItems.has(item.id)}
                          >
                            <Plus className="h-2.5 w-2.5" />
                          </Button>
                        </div>
                        <p className="text-xs font-semibold text-foreground">
                          {formatCurrency(item.totalPrice, cart.currencyCode)}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </ScrollArea>

        {/* Cart Summary */}
        <div className="border-t border-border pt-3 space-y-2 flex-shrink-0">
          <div className="space-y-1.5">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Subtotal:</span>
              <span className="text-sm text-foreground">
                {formatCurrency(cart.subtotalAmount, cart.currencyCode)}
              </span>
            </div>
            {/* Order Discounts */}
            {cart.items.filter(item => item.orderLineType === 'discount').map(discount => (
              <div key={discount.id} className="flex justify-between items-center text-green-600 dark:text-green-400">
                <span className="text-xs">{discount.productName}:</span>
                <span className="text-xs font-medium">
                  {formatCurrency(discount.totalPrice, cart.currencyCode)}
                </span>
              </div>
            ))}
            {/* Taxes */}
            {cart.items.filter(item => item.orderLineType?.toLowerCase() === 'tax').map(tax => (
              <div key={tax.id} className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">{tax.productName}:</span>
                <span className="text-xs text-foreground">
                  {formatCurrency(tax.totalPrice, cart.currencyCode)}
                </span>
              </div>
            ))}
            {cart.shippingFee > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  Shipping ({cart.shippingMethod?.name || 'Standard'}):
                </span>
                <span className="text-sm text-foreground">
                  {formatCurrency(cart.shippingFee, cart.currencyCode)}
                </span>
              </div>
            )}
            {cart.paymentFee > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  Payment Fee ({cart.paymentMethod?.name || 'Standard'}):
                </span>
                <span className="text-sm text-foreground">
                  {formatCurrency(cart.paymentFee, cart.currencyCode)}
                </span>
              </div>
            )}
            <div className="border-t border-border pt-2">
              <div className="flex justify-between items-center">
                <span className="font-medium text-foreground">Total:</span>
                <span className="font-semibold text-lg text-foreground">
                  {formatCurrency(cart.totalAmount, cart.currencyCode)}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-1.5">
            <Button 
              className="w-full" 
              size="lg"
              onClick={() => {
                setIsOpen(false);
                window.location.href = '/checkout';
              }}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Go to Checkout
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <Button 
                variant="outline" 
                className="w-full" 
                onClick={() => {
                  setIsOpen(false);
                  window.location.href = '/cart';
                }}
              >
                <Eye className="h-4 w-4 mr-2" />
                See Cart
              </Button>
              <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Cart
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Save Cart</DialogTitle>
                    <DialogDescription>
                      Give your cart a name to save it for later.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="cart-name">Cart Name</Label>
                      <Input
                        id="cart-name"
                        placeholder="Enter cart name..."
                        value={cartName}
                        onChange={(e) => setCartName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveCart();
                          }
                        }}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setShowSaveDialog(false);
                        setCartName('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleSaveCart}>
                      Save Cart
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        {children}
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader className="space-y-2 flex-shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Shopping Cart
            {totalItems > 0 && (
              <Badge variant="secondary" className="ml-2">
                {totalItems}
              </Badge>
            )}
          </SheetTitle>
          <SheetDescription>
            {cart ? 
              `Review your items and proceed to checkout` : 
              'Your shopping cart is ready for items'
            }
          </SheetDescription>
        </SheetHeader>
        
        <div className="mt-6 flex-1 overflow-hidden flex flex-col">
          {renderCartContent()}
        </div>
      </SheetContent>
    </Sheet>
  );
}